import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { useLanguage } from '@/contexts/LanguageContext';
import { Mail, Phone, MessageCircle, Send, Copy, Check, Clock } from 'lucide-react';

interface ContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactDialog({ open, onOpenChange }: ContactDialogProps) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const contactInfo = {
    email: 'info@sourcemygoods.com',
    phone: '+86 130 4806 4436',
    whatsapp: '8613048064436'
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(contactInfo.email);
      setCopiedEmail(true);
      toast({
        title: t('emailCopied'),
        description: t('emailAddressCopied'),
      });
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch (err) {
      toast({
        title: 'Failed to copy',
        variant: 'destructive',
      });
    }
  };

  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(contactInfo.phone);
      setCopiedPhone(true);
      toast({
        title: t('phoneCopied'),
        description: t('phoneNumberCopied'),
      });
      setTimeout(() => setCopiedPhone(false), 2000);
    } catch (err) {
      toast({
        title: 'Failed to copy',
        variant: 'destructive',
      });
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hi, I'd like to inquire about your sourcing and shipping services.");
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${message}`, '_blank');
  };

  const handleCallNow = () => {
    window.location.href = `tel:${contactInfo.phone}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = encodeURIComponent('Contact Request from SourceMyGoods Website');
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
    
    toast({
      title: t('openingEmailClient'),
      description: t('defaultEmailWillOpen'),
    });
    
    // Reset form
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('getInTouch')}</DialogTitle>
          <DialogDescription>
            {t('chooseContactMethod')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto">
          {/* Email Contact */}
          <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 mb-1">{t('emailUs')}</h3>
              <p className="text-sm text-gray-600 mb-2">{t('responseWithin24Hours')}</p>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-blue-700 bg-white px-2 py-1 rounded">
                  {contactInfo.email}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCopyEmail}
                  className="h-7"
                >
                  {copiedEmail ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Phone Contact */}
          <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <div className="h-12 w-12 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 mb-1">{t('callUs')}</h3>
              <p className="text-sm text-gray-600 mb-2">{t('directPhoneSupport')}</p>
              <div className="flex items-center gap-2 mb-2">
                <code className="text-sm font-mono text-green-700 bg-white px-2 py-1 rounded">
                  {contactInfo.phone}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCopyPhone}
                  className="h-7"
                >
                  {copiedPhone ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <Button
                size="sm"
                onClick={handleCallNow}
                className="bg-green-600 hover:bg-green-700"
              >
                <Phone className="h-3 w-3 mr-1" />
                {t('callNow')}
              </Button>
            </div>
          </div>

          {/* WhatsApp Contact */}
          <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
            <div className="h-12 w-12 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 mb-1">{t('whatsappBusiness')}</h3>
              <p className="text-sm text-gray-600 mb-2">{t('chatWithTeam')}</p>
              <Button
                size="sm"
                onClick={handleWhatsApp}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <MessageCircle className="h-3 w-3 mr-1" />
                {t('openWhatsApp')}
              </Button>
            </div>
          </div>

          {/* Business Hours */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="h-12 w-12 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">{t('businessHours')}</h3>
              <p className="text-sm text-gray-600">{t('businessHoursTime')}</p>
            </div>
          </div>

          {/* Quick Message Form */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">{t('sendQuickMessage')}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">{t('yourName')}</Label>
                  <Input
                    id="contact-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">{t('yourEmail')}</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">{t('message')}</Label>
                <Textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t('tellUsAboutNeeds')}
                  rows={4}
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                <Send className="h-4 w-4 mr-2" />
                {t('sendMessage')}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                {t('opensEmailClient')}
              </p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
